import { useEffect } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { artistFetch } from '@/lib/artist-api'

interface Props {
  showId: string
  stage: string
  integrations: {
    hiveCells?: {
      showId?: string
      status?: string
      syncedAt?: string
      lastUpdatedAt?: string
    }
  } | null
}

interface Status {
  connected: boolean
  cellSlug?: string
  cellUrl?: string
}

/**
 * Panel rendered on the show detail page that displays Hive Cells sync status
 * and provides a link to the Hive Cells admin for the synced show.
 *
 * Returns null if the artist has no active Hive Cells integration.
 */
export function HiveCellsPanel({ showId, stage, integrations }: Props) {
  const qc = useQueryClient()
  const statusQ = useQuery({
    queryKey: ['integrations', 'hive-cells'],
    queryFn: () => artistFetch<Status>(`/integrations/hive-cells`),
  })

  const hc = integrations?.hiveCells
  const synced = !!hc?.showId
  const shouldPoll = stage === 'confirmed' && !synced

  useEffect(() => {
    if (!shouldPoll) return
    const id = window.setInterval(() => {
      qc.invalidateQueries({ queryKey: ['show-brief', showId] })
    }, 30_000)
    return () => window.clearInterval(id)
  }, [shouldPoll, qc, showId])

  if (!statusQ.data?.connected) return null

  return (
    <section className="rounded-lg border border-ovation-ink/10 bg-white p-5">
      <div className="mb-3 flex items-center justify-between">
        <h3 className="text-xs uppercase tracking-wider text-ovation-muted">
          Hive Cells
        </h3>
        {synced && <StatusBadge status={hc?.status ?? 'synced'} />}
      </div>

      {!synced && stage !== 'confirmed' && stage !== 'completed' && (
        <p className="text-sm text-ovation-muted">
          This show will be published to Hive Cells automatically when it reaches{' '}
          <strong className="text-ovation-ink">confirmed</strong>.
        </p>
      )}

      {!synced && (stage === 'confirmed' || stage === 'completed') && (
        <p className="text-sm text-amber-600">
          Waiting for sync to Hive Cells. If this persists, check the worker logs.
        </p>
      )}

      {synced && (
        <div className="space-y-2 text-sm">
          <div className="text-ovation-muted">
            Published to Hive Cells
            {hc?.syncedAt && (
              <> on {new Date(hc.syncedAt).toLocaleString()}</>
            )}
          </div>
          {statusQ.data.cellUrl && (
            <a
              href={`${statusQ.data.cellUrl}/admin/shows/${hc?.showId}`}
              target="_blank"
              rel="noreferrer"
              className="inline-block text-ovation-accent underline hover:opacity-80"
            >
              Open in Hive Cells admin →
            </a>
          )}
        </div>
      )}
    </section>
  )
}

function StatusBadge({ status }: { status: string }) {
  const cls =
    status === 'live'
      ? 'bg-green-100 text-green-800'
      : status === 'cancelled'
        ? 'bg-red-100 text-red-800'
        : 'bg-blue-100 text-blue-800'
  return (
    <span className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${cls}`}>
      {status}
    </span>
  )
}
